import { ReactNode } from "react";
import { Share2 } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { AuroraAtmosphere, AuroraGauge } from "@/components/home/AuroraArc";
import { useGaugeSweep } from "@/components/home/LayerIndexCard";
import { stateColor, signed, deltaColor } from "./primitives";

const StatCell = ({ label, testid, children }: { label: string; testid: string; children: ReactNode }) => (
  <div data-testid={testid}>
    <p className="nl-mono text-[9px] uppercase tracking-[0.22em] text-gray-600">{label}</p>
    <div className="mt-1">{children}</div>
  </div>
);

export const LIHero = ({ data, onShare }: { data: any; onShare: () => void }) => {
  const cur = data?.current;
  const value: number | null = cur?.value ?? null;
  const { display, done, glint } = useGaugeSweep(value, !!cur);
  const pct = Math.max(0, Math.min(display, 100));
  const num = done && value != null ? value : Math.round(Math.min(pct, value ?? 100));
  const color = stateColor(cur?.label);
  const slu = data?.since_last_update;
  return (
    <section className="relative overflow-hidden rounded-2xl border border-[#1c212e] bg-black" data-testid="li-hero">
      <AuroraAtmosphere />
      <div className="relative grid lg:grid-cols-[380px_minmax(0,1fr)] gap-8 lg:gap-12 p-6 sm:p-10 items-center">
        <div className="mx-auto w-full max-w-[320px] lg:max-w-none">
          <AuroraGauge pct={pct} animating={!!cur && !done} glint={glint}
            num={value == null ? "—" : num} label={cur?.label} labelColor={color}
            delta={cur?.change_24h} done={done} dormant={!cur} />
          <span className="sr-only" data-testid="index-page-value">{value == null ? "—" : value}</span>
        </div>
        <div className="min-w-0">
          <p className="nl-mono text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">
            NewsLayer Proprietary Instrument · Methodology v{cur?.methodology_version || "1.0"}
          </p>
          <h1 className="font-news font-black text-white text-4xl sm:text-5xl tracking-tight mt-2">
            The Layer Index
          </h1>
          <p className="text-gray-400 text-sm sm:text-[15px] leading-relaxed mt-3 max-w-2xl" data-testid="li-hero-reading">
            {cur?.reading || "A live 0–100 reading of crypto market mood, computed from NewsLayer coverage and live market data."}
          </p>
          <div className="mt-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-10 gap-y-4">
            <StatCell label="24H" testid="li-hero-24h">
              <span className="nl-sg text-xl font-bold" style={{ color: deltaColor(cur?.change_24h) }}>
                {signed(cur?.change_24h)}{cur?.change_24h != null ? " pts" : ""}
              </span>
            </StatCell>
            <StatCell label="7D" testid="li-hero-7d">
              <span className="nl-sg text-xl font-bold" style={{ color: deltaColor(cur?.change_7d) }}>
                {signed(cur?.change_7d)}{cur?.change_7d != null ? " pts" : ""}
              </span>
            </StatCell>
            <StatCell label="State" testid="li-hero-state">
              <span className="nl-mono text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color }}>
                {cur?.label || "—"}
              </span>
            </StatCell>
            <StatCell label="Updated" testid="li-hero-updated">
              <span className="nl-mono text-[12px] text-gray-300">
                {cur?.computed_at ? timeAgo(cur.computed_at) : "—"}
              </span>
              <span className="block nl-mono text-[9px] text-gray-600 mt-0.5">
                refreshes ~every {cur?.update_minutes || 30} min
              </span>
            </StatCell>
          </div>
          {slu && (
            <div className="mt-6 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3 max-w-2xl"
              data-testid="li-since-update">
              <p className="nl-mono text-[9px] uppercase tracking-[0.25em] text-gray-500">Since last update</p>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1.5">
                <span className="nl-sg text-lg font-bold shrink-0" style={{ color: deltaColor(slu.delta) }}>
                  {slu.delta === 0 ? "Unchanged" : `${signed(slu.delta)} pts`}
                </span>
                <span className="text-[13px] text-gray-400 leading-snug">{slu.text}</span>
              </div>
            </div>
          )}
          <button type="button" onClick={onShare} data-testid="index-share-button"
            className="mt-7 inline-flex items-center gap-2 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/[0.06] text-white font-semibold text-sm px-4 py-2 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.14] transition-colors">
            <Share2 className="h-4 w-4 text-[#00E5FF]" /> Share today's index
          </button>
        </div>
      </div>
    </section>
  );
};
